import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import {
    Box,
    Typography,
    Paper,
    Button,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Divider,
    Tab,
    Tabs,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useMenuAdmin, MenuItemDto } from "src/api/client-api/menu/useMenuAdmin";
import { useSnackbar } from "src/utils/useSnackbar";
import { MenuItemForm } from "./MenuItemForm";
import { DraggableMenuTree } from "./DraggableMenuTree";
import { MenuItemFormSubmit } from "./Form.types";
import { CREATE, DELETE, MODIFY, NAVIGATION_MENU, useFoodpilotContext } from "src/context/FoodpilotContext";

type MenuTreeNodeWithAdmin = {
    item: MenuItemDto;
    children: MenuTreeNodeWithAdmin[];
};

type MenuType = "main" | "footer";

export const MenuManagement = () => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const queryClient = useQueryClient();
    const { canUser } = useFoodpilotContext();
    const canCreate = canUser(CREATE, NAVIGATION_MENU);
    const canModify = canUser(MODIFY, NAVIGATION_MENU);
    const canDelete = canUser(DELETE, NAVIGATION_MENU);

    const {
        menus,
        isLoading: isLoadingMenus,
        createMenuItem,
        updateMenuItem,
        deleteMenuItem,
        bulkReorder,
        isCreating,
        isUpdating,
        isDeleting,
        isReordering,
    } = useMenuAdmin();

    const [selectedMenuType, setSelectedMenuType] = useState<MenuType>("main");
    const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
    const [formOpen, setFormOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<MenuItemDto | null>(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

    // Get current menu based on selected type
    const currentMenu = useMemo(() => {
        return menus.find((menu) => menu.type === selectedMenuType);
    }, [menus, selectedMenuType]);

    // Filter menu items by current menu
    // Use API filter to get items for current menu
    const menuItemsQuery = useQuery({
        queryKey: ["menuItems", currentMenu?.id],
        queryFn: async (): Promise<MenuItemDto[]> => {
            if (!currentMenu) return [];
            const response = await loggedInApiCall.get(`/api/menu_items?menu=${currentMenu.id}`);
            const raw = response.data["hydra:member"] || response.data;
            return (raw as any[]).map((it: any) => ({
                ...it,
                i18nKey: it.i18nKey ?? (typeof it.label === "string" && it.label.includes(".") ? it.label : undefined),
            }));
        },
        enabled: !!currentMenu,
    });

    const menuItems = menuItemsQuery.data || [];

    // Build hierarchical tree from filtered menu items
    const menuTree = useMemo(() => {
        const buildTree = (items: MenuItemDto[], parentId: number | null = null): MenuTreeNodeWithAdmin[] => {
            return items
                .filter((item) => (parentId === null ? !item.parent : item.parent?.id === parentId))
                .sort((a, b) => a.weight - b.weight)
                .map((item) => ({
                    item,
                    children: buildTree(items, item.id),
                }));
        };

        return buildTree(menuItems);
    }, [menuItems]);

    const isLoading = isLoadingMenus || menuItemsQuery.isLoading;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: MenuType) => {
        setSelectedMenuType(newValue);
        setExpandedItems(new Set()); // Reset expanded items when switching tabs
    };

    const toggleExpanded = (itemId: number) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const handleCreateItem = () => {
        if (!canCreate) return;

        setEditingItem(null);
        setFormOpen(true);
    };

    const handleEditItem = (item: MenuItemDto) => {
        if (!canModify) return;

        setEditingItem(item);
        setFormOpen(true);
    };

    const handleDeleteItem = (itemId: number) => {
        if (!canDelete) return;

        setDeleteConfirmId(itemId);
    };

    const confirmDelete = async () => {
        if (!canDelete || !deleteConfirmId) return;

        try {
            await deleteMenuItem.mutateAsync(deleteConfirmId);
            // Invalidate menu items query for current menu
            queryClient.invalidateQueries(["menuItems", currentMenu?.id]);
            snackbar.setSnackbarProps({
                type: "success",
                message: t("menu.item.deleted", "Menu item deleted successfully"),
            });
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("menu.item.delete.error", "Error deleting menu item"),
            });
        } finally {
            setDeleteConfirmId(null);
        }
    };

    const handleFormSubmit = async (data: MenuItemFormSubmit) => {
        if ((editingItem && !canModify) || (!editingItem && !canCreate)) return;

        try {
            if (editingItem) {
                await updateMenuItem.mutateAsync(
                    editingItem.moduleFeatureKey ?
                        { id: editingItem.id, parentId: data.parentId }
                    :   { id: editingItem.id, ...data },
                );
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("menu.item.updated", "Menu item updated successfully"),
                });
            } else {
                await createMenuItem.mutateAsync({
                    ...data,
                    menuId: currentMenu?.id || 1,
                });
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("menu.item.created", "Menu item created successfully"),
                });
            }
            queryClient.invalidateQueries(["menuItems", currentMenu?.id]);
            setFormOpen(false);
        } catch (error) {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("menu.item.save.error", "Error saving menu item"),
            });
        }
    };

    const handleReorder = async (reorderData: {
        items: Array<{
            id: number;
            weight: number;
            parentId: number | null;
        }>;
    }) => {
        if (!canModify) return;

        await bulkReorder.mutateAsync({
            ...reorderData,
            menuId: currentMenu?.id,
        });
        // Invalidate menu items query for current menu
        queryClient.invalidateQueries(["menuItems", currentMenu?.id]);
    };

    if (isLoading) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography>Loading menu data...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 3 }}>
                {t("menu.management.title", "Menu Management")}
            </Typography>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                <Tabs
                    value={selectedMenuType}
                    onChange={handleTabChange}
                    aria-label="Menu type tabs"
                    sx={{
                        "& .MuiTab-root": {
                            color: "#393A36",
                            textTransform: "none",
                            fontSize: "1rem",
                            fontWeight: 500,
                        },
                        "& .MuiTab-root.Mui-selected": {
                            color: (theme) => theme.palette.primary[600],
                        },
                        "& .MuiTabs-indicator": {
                            backgroundColor: (theme) => theme.palette.primary[600],
                        },
                    }}
                >
                    <Tab label={t("menu.type.main", "Main Menu")} value="main" sx={{ textTransform: "none" }} />
                    <Tab label={t("menu.type.footer", "Footer Menu")} value="footer" sx={{ textTransform: "none" }} />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box>
                {!currentMenu && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {t(
                            "menu.no.menu",
                            `No ${selectedMenuType} menu found. The menu will be created automatically when you add the first item.`,
                        )}
                    </Alert>
                )}

                <Paper sx={{ p: 2, mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Typography variant="h6">{t("menu.structure", "Menu Structure")}</Typography>
                        {canCreate && (
                            <Button
                                variant="contained"
                                startIcon={<FoodsIcon size={2} icon="plus" />}
                                onClick={handleCreateItem}
                                disabled={!currentMenu}
                                sx={(theme) => ({
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    textTransform: "none",
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                        color: "white !important",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                })}
                            >
                                {t("menu.item.add", "Add Menu Item")}
                            </Button>
                        )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    <DraggableMenuTree
                        menuTree={menuTree}
                        expandedItems={expandedItems}
                        onToggleExpanded={toggleExpanded}
                        onEditItem={handleEditItem}
                        onDeleteItem={handleDeleteItem}
                        onReorder={handleReorder}
                        canModify={canModify}
                        canDelete={canDelete}
                    />
                </Paper>

                {/* Create/Edit Dialog */}
                <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        {editingItem ?
                            t("menu.item.edit", "Edit Menu Item")
                        :   t("menu.item.create", "Create Menu Item")}
                    </DialogTitle>

                    <DialogContent>
                        <MenuItemForm
                            initialData={editingItem}
                            menuItems={menuItems}
                            onSubmit={handleFormSubmit}
                            onCancel={() => setFormOpen(false)}
                            isLoading={isCreating || isUpdating}
                        />
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation */}
                <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
                    <DialogTitle>{t("menu.item.delete.confirm.title", "Delete Menu Item")}</DialogTitle>

                    <DialogContent>
                        <Typography>
                            {t(
                                "menu.item.delete.confirm.message",
                                "Are you sure you want to delete this menu item? This action cannot be undone.",
                            )}
                        </Typography>
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setDeleteConfirmId(null)}>{t("cancel", "Cancel")}</Button>
                        <Button onClick={confirmDelete} color="error" variant="contained" disabled={isDeleting}>
                            {t("delete", "Delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
};
